/** IAU-style stick figures for Credence Atlas (J2000, simplified asterisms). */

export interface AtlasConstellation {
  id: string;
  name: string;
  /** Primary stick figure segments. */
  major: [number, number][][];
  /** Secondary / lesser connecting paths. */
  minor?: [number, number][][];
  /** Label anchor [RA°, Dec°]; defaults to centroid of major paths. */
  label?: [number, number];
}

export const ATLAS_CONSTELLATION_DATA: AtlasConstellation[] = [
  {
    id: 'ursa-major',
    name: 'Ursa Major',
    label: [175, 55],
    major: [
      [[165.93, 61.75], [165.46, 56.38], [178.46, 53.69], [183.86, 57.03], [193.51, 55.96], [200.98, 54.92], [206.89, 49.31]],
    ],
    minor: [
      [[183.86, 57.03], [178.46, 53.69]],
      [[193.51, 55.96], [183.86, 57.03]],
    ],
  },
  {
    id: 'ursa-minor',
    name: 'Ursa Minor',
    label: [225, 77],
    major: [
      [[37.95, 89.26], [263.05, 86.59], [247.55, 86.59], [222.68, 74.16], [230.08, 71.83], [37.95, 89.26]],
    ],
    minor: [[[222.68, 74.16], [247.55, 86.59]]],
  },
  {
    id: 'cassiopeia',
    name: 'Cassiopeia',
    label: [15, 60],
    major: [[[17.24, 59.09], [10.13, 56.53], [14.17, 60.72], [21.45, 60.23], [28.6, 63.67]]],
  },
  {
    id: 'cepheus',
    name: 'Cepheus',
    label: [325, 62],
    major: [
      [[319.65, 58.2], [337.23, 66.2], [344.41, 71.83], [354.84, 61.51], [331.45, 58.78], [319.65, 58.2]],
    ],
    minor: [[[344.41, 71.83], [337.23, 66.2]]],
  },
  {
    id: 'draco',
    name: 'Draco',
    label: [250, 65],
    major: [
      [[211.67, 69.33], [219.75, 72.73], [243.43, 69.66], [263.05, 86.59], [288.16, 67.66], [300.45, 54.92], [288.16, 67.66]],
    ],
    minor: [[[243.43, 69.66], [211.67, 69.33]]],
  },
  {
    id: 'orion',
    name: 'Orion',
    label: [81, 0],
    major: [
      [[88.79, 7.41], [81.28, 6.35], [84.05, -1.2], [85.19, -1.94], [78.63, -8.2]],
      [[84.05, -1.2], [86.94, -9.67], [88.79, 7.41]],
    ],
    minor: [
      [[81.28, 6.35], [78.63, -8.2]],
      [[85.19, -1.94], [83.0, -5.4]],
    ],
  },
  {
    id: 'taurus',
    name: 'Taurus',
    label: [60, 20],
    major: [
      [[68.98, 16.51], [81.57, 28.61], [56.75, 24.12], [66.75, 15.87], [68.98, 16.51]],
      [[68.98, 16.51], [51.08, 49.86]],
    ],
    minor: [[[56.75, 24.12], [51.08, 49.86]]],
  },
  {
    id: 'gemini',
    name: 'Gemini',
    label: [115, 28],
    major: [
      [[113.65, 31.89], [116.33, 28.03], [100.98, 16.4], [113.65, 31.89]],
      [[116.33, 28.03], [93.71, 22.51], [100.98, 16.4]],
    ],
    minor: [[[93.71, 22.51], [92.58, 24.35]]],
  },
  {
    id: 'cancer',
    name: 'Cancer',
    label: [130, 20],
    major: [[[130.08, 19.78], [124.13, 9.19], [152.09, 11.97], [131.17, 28.76], [130.08, 19.78]]],
    minor: [[[124.13, 9.19], [152.09, 11.97]]],
  },
  {
    id: 'leo',
    name: 'Leo',
    label: [152, 15],
    major: [
      [[152.09, 11.97], [154.99, 23.77], [168.53, 20.52], [177.26, 14.57], [152.09, 11.97]],
      [[168.53, 20.52], [176.87, 6.03], [152.09, 11.97]],
    ],
    minor: [[[176.87, 6.03], [177.26, 14.57]]],
  },
  {
    id: 'virgo',
    name: 'Virgo',
    label: [195, 0],
    major: [
      [[201.3, -11.16], [193.9, -0.67], [176.63, 6.53], [201.3, -11.16]],
      [[193.9, -0.67], [186.28, -0.79], [176.63, 6.53]],
    ],
    minor: [[[186.28, -0.79], [201.3, -11.16]]],
  },
  {
    id: 'bootes',
    name: 'Boötes',
    label: [218, 25],
    major: [[[213.92, 19.18], [225.46, 40.39], [213.92, 19.18], [204.97, 27.07], [213.92, 19.18]]],
    minor: [[[225.46, 40.39], [204.97, 27.07]]],
  },
  {
    id: 'hercules',
    name: 'Hercules',
    label: [260, 30],
    major: [
      [[258.26, 14.39], [244.58, 21.49], [263.05, 46.31], [279.73, 30.93], [258.26, 14.39]],
    ],
    minor: [[[244.58, 21.49], [279.73, 30.93]]],
  },
  {
    id: 'lyra',
    name: 'Lyra',
    label: [282, 36],
    major: [[[279.24, 38.78], [281.23, 33.36], [282.52, 36.9], [279.24, 38.78]]],
  },
  {
    id: 'cygnus',
    name: 'Cygnus',
    label: [305, 38],
    major: [
      [[310.36, 45.28], [299.69, 27.96], [292.68, 33.97], [310.36, 45.28]],
      [[299.69, 27.96], [305.56, 40.26], [310.36, 45.28]],
    ],
    minor: [[[292.68, 33.97], [305.56, 40.26]]],
  },
  {
    id: 'aquila',
    name: 'Aquila',
    label: [297, 5],
    major: [[[279.87, 8.87], [297.7, 10.61], [279.87, 8.87], [286.35, 13.86], [279.87, 8.87]]],
    minor: [[[297.7, 10.61], [286.35, 13.86]]],
  },
  {
    id: 'pegasus',
    name: 'Pegasus',
    label: [345, 20],
    major: [
      [[346.19, 9.19], [354.84, 12.17], [2.1, 15.18], [345.94, 15.18], [346.19, 9.19]],
    ],
    minor: [[[2.1, 15.18], [354.84, 12.17]]],
  },
  {
    id: 'andromeda',
    name: 'Andromeda',
    label: [20, 40],
    major: [
      [[2.1, 15.18], [17.43, 35.62], [30.97, 42.33], [30.74, 45.87], [2.1, 15.18]],
    ],
    minor: [[[17.43, 35.62], [30.74, 45.87]]],
  },
  {
    id: 'perseus',
    name: 'Perseus',
    label: [48, 50],
    major: [
      [[51.08, 49.86], [47.04, 40.96], [40.68, 42.76], [34.75, 57.15], [51.08, 49.86]],
    ],
    minor: [[[47.04, 40.96], [34.75, 57.15]]],
  },
  {
    id: 'auriga',
    name: 'Auriga',
    label: [88, 42],
    major: [
      [[79.17, 46.0], [81.57, 28.61], [100.98, 16.4], [79.17, 46.0], [88.0, 54.0], [79.17, 46.0]],
    ],
    minor: [[[100.98, 16.4], [81.57, 28.61]]],
  },
  {
    id: 'canis-major',
    name: 'Canis Major',
    label: [105, -20],
    major: [
      [[101.29, -16.72], [105.76, -23.83], [95.67, -17.96], [101.29, -16.72]],
      [[101.29, -16.72], [104.66, -28.98]],
    ],
    minor: [[[95.67, -17.96], [104.66, -28.98]]],
  },
  {
    id: 'canis-minor',
    name: 'Canis Minor',
    label: [115, 5],
    major: [[[114.83, 5.23], [111.79, 8.29], [114.83, 5.23]]],
  },
  {
    id: 'scorpius',
    name: 'Scorpius',
    label: [255, -30],
    major: [
      [[247.35, -26.43], [252.17, -34.29], [263.4, -37.1], [266.41, -37.3], [264.33, -42.36], [252.97, -41.17], [247.35, -26.43]],
    ],
    minor: [[[266.41, -37.3], [252.97, -41.17]]],
  },
  {
    id: 'sagittarius',
    name: 'Sagittarius',
    label: [275, -25],
    major: [
      [[276.04, -29.88], [283.82, -26.3], [276.99, -16.04], [266.62, -26.3], [276.04, -29.88]],
      [[276.99, -16.04], [283.82, -26.3]],
    ],
    minor: [[[266.62, -26.3], [276.04, -29.88]]],
  },
  {
    id: 'ophiuchus',
    name: 'Ophiuchus',
    label: [260, 5],
    major: [
      [[258.84, 9.65], [263.73, 12.56], [247.73, 6.2], [258.84, 9.65], [263.05, -15.72], [258.84, 9.65]],
    ],
    minor: [[[247.73, 6.2], [263.05, -15.72]]],
  },
  {
    id: 'corona-borealis',
    name: 'Corona Borealis',
    label: [235, 30],
    major: [[[233.67, 26.71], [236.02, 29.11], [238.63, 31.36], [241.36, 29.11], [233.67, 26.71]]],
  },
  {
    id: 'delphinus',
    name: 'Delphinus',
    label: [309, 12],
    major: [[[309.38, 15.91], [312.46, 15.91], [309.38, 10.83], [306.3, 15.91], [309.38, 15.91]]],
  },
  {
    id: 'crux',
    name: 'Crux',
    label: [187, -60],
    major: [[[186.65, -63.1], [187.79, -57.11], [189.71, -58.75], [186.65, -63.1]]],
  },
  {
    id: 'centaurus',
    name: 'Centaurus',
    label: [205, -50],
    major: [
      [[210.96, -60.37], [186.65, -63.1], [219.9, -60.84], [210.96, -60.37]],
      [[219.9, -60.84], [211.67, -36.37], [210.96, -60.37]],
    ],
    minor: [[[211.67, -36.37], [186.65, -63.1]]],
  },
  {
    id: 'carina',
    name: 'Carina',
    label: [130, -60],
    major: [
      [[95.99, -52.7], [125.63, -59.51], [138.3, -69.72], [161.0, -64.4], [95.99, -52.7]],
    ],
    minor: [[[125.63, -59.51], [161.0, -64.4]]],
  },
  {
    id: 'vela',
    name: 'Vela',
    label: [135, -45],
    major: [[[136.0, -54.7], [141.1, -47.0], [148.0, -42.5], [136.0, -54.7]]],
    minor: [[[141.1, -47.0], [136.0, -54.7]]],
  },
  {
    id: 'puppis',
    name: 'Puppis',
    label: [120, -30],
    major: [[[116.0, -24.0], [125.0, -32.0], [110.0, -35.0], [116.0, -24.0]]],
  },
  {
    id: 'hydra',
    name: 'Hydra',
    label: [170, -10],
    major: [
      [[141.9, -8.66], [152.09, 11.97], [176.63, 6.53], [198.0, -23.0], [220.0, -35.0], [141.9, -8.66]],
    ],
    minor: [[[176.63, 6.53], [198.0, -23.0]]],
  },
  {
    id: 'cetus',
    name: 'Cetus',
    label: [20, -10],
    major: [
      [[26.02, -15.94], [10.13, 8.91], [2.0, -8.0], [26.02, -15.94], [35.0, -12.0], [26.02, -15.94]],
    ],
    minor: [[[10.13, 8.91], [2.0, -8.0]]],
  },
  {
    id: 'triangulum',
    name: 'Triangulum',
    label: [30, 32],
    major: [[[28.17, 29.09], [34.33, 34.2], [28.17, 29.09], [20.0, 35.0], [28.17, 29.09]]],
  },
  {
    id: 'corona-australis',
    name: 'Corona Australis',
    label: [275, -42],
    major: [[[274.4, -37.9], [276.0, -42.0], [278.0, -45.0], [274.4, -37.9]]],
  },
  {
    id: 'lupus',
    name: 'Lupus',
    label: [230, -40],
    major: [[[229.25, -36.26], [236.0, -47.0], [220.0, -43.0], [229.25, -36.26]]],
  },
  {
    id: 'norma',
    name: 'Norma',
    label: [240, -50],
    major: [[[239.0, -50.0], [245.0, -55.0], [235.0, -58.0], [239.0, -50.0]]],
  },
  {
    id: 'grus',
    name: 'Grus',
    label: [340, -45],
    major: [[[340.67, -46.88], [346.0, -37.0], [335.0, -52.0], [340.67, -46.88]]],
  },
  {
    id: 'phoenix',
    name: 'Phoenix',
    label: [25, -50],
    major: [[[18.0, -46.0], [30.0, -42.0], [22.0, -55.0], [18.0, -46.0]]],
  },
  {
    id: 'eridanus',
    name: 'Eridanus',
    label: [50, -10],
    major: [
      [[28.6, -51.51], [50.0, -35.0], [70.0, -20.0], [90.0, -10.0], [110.0, -5.0], [130.0, -10.0], [150.0, -20.0], [28.6, -51.51]],
    ],
    minor: [[[90.0, -10.0], [70.0, -20.0]]],
  },
  {
    id: 'leo-minor',
    name: 'Leo Minor',
    label: [150, 33],
    major: [[[151.0, 34.0], [156.0, 36.0], [148.0, 37.0], [151.0, 34.0]]],
  },
  {
    id: 'coma-berenices',
    name: 'Coma Berenices',
    label: [185, 25],
    major: [[[185.0, 28.0], [190.0, 25.0], [180.0, 22.0], [185.0, 28.0]]],
  },
  {
    id: 'libra',
    name: 'Libra',
    label: [230, -15],
    major: [[[222.72, -16.04], [233.67, -25.28], [222.72, -16.04], [215.0, -20.0], [222.72, -16.04]]],
  },
  {
    id: 'capricornus',
    name: 'Capricornus',
    label: [315, -15],
    major: [[[315.0, -17.0], [325.0, -12.0], [310.0, -10.0], [315.0, -17.0]]],
  },
  {
    id: 'aquarius',
    name: 'Aquarius',
    label: [340, -5],
    major: [
      [[331.0, -0.3], [340.0, -5.0], [350.0, -10.0], [331.0, -0.3], [325.0, 2.0], [331.0, -0.3]],
    ],
    minor: [[[340.0, -5.0], [325.0, 2.0]]],
  },
  {
    id: 'pisces',
    name: 'Pisces',
    label: [30, 5],
    major: [
      [[30.0, 5.0], [10.0, 10.0], [350.0, 5.0], [30.0, 5.0], [25.0, -5.0], [30.0, 5.0]],
    ],
    minor: [[[350.0, 5.0], [10.0, 10.0]]],
  },
  {
    id: 'aries',
    name: 'Aries',
    label: [30, 20],
    major: [[[28.66, 20.81], [31.79, 23.46], [28.66, 20.81], [2.0, 20.0], [28.66, 20.81]]],
  },
  {
    id: 'sculptor',
    name: 'Sculptor',
    label: [10, -30],
    major: [[[15.0, -28.0], [5.0, -32.0], [12.0, -35.0], [15.0, -28.0]]],
  },
  {
    id: 'fornax',
    name: 'Fornax',
    label: [45, -30],
    major: [[[45.0, -28.0], [50.0, -32.0], [40.0, -35.0], [45.0, -28.0]]],
  },
];
